/**
 * File: apps/qbench-cp/reactapp/src/shared/components/FormV2/Input.js
 *
 * Purpose: Renders the base text input used directly by FormV2's STR field type and reused by
 *     Number and Hyperlink for their own field types.
 *
 * Scope: FormV2 input rendering for text-type entity fields, plus shared read-only styling.
 *
 * Tags: customer-portal
 *
 * Overview: Exports Input, a memoized component that registers itself with react-hook-form via
 *     the passed-in `register(name, { required })` function, applying read-only styling and
 *     the readOnlyAttrs (readOnly, disabled, and a title tooltip) when the field is read-only.
 *     Also exports readOnlyAttrs for reuse by other FormV2 field components. The attached
 *     `transform` strategy is an identity pass-through for fromServer/toServer/defaultValue.
 *
 * Exports: default Input component (with an attached `transform` static property); named
 *     readOnlyAttrs constant.
 */

import React from 'react';
import classNames from 'classnames';
import { domProps } from 'shared/utils/form';

export const readOnlyAttrs = {
  readOnly: true,
  disabled: true,
  title: 'This field is read-only',
};

/**
 * @type {import('./types').FieldTransformStrategy}
 */
const transform = {
  fromServer(value) {
    return value;
  },
  toServer(value) {
    const payload = value;
    return payload;
  },
  defaultValue(value) {
    return value;
  },
};

const Input = React.memo(function Input({
  name,
  register,
  displayName,
  required = false,
  readOnly,
  ...props
}) {
  return (
    <input
      id={name}
      className={classNames(
        'w-100 h-input p-2 mb-4 fs-7 fw-normal lh-sm border-0 border-bottom border-input',
        readOnly && 'bg-light text-muted cursor-not-allowed'
      )}
      placeholder={displayName}
      {...register(name, { required })}
      {...(readOnly && readOnlyAttrs)}
      {...domProps(props)}
    />
  );
});

Input.transform = transform;
export default Input;
